import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDeploymentViewModel } from '../ViewModel/DeploymentViewModel';
import { Site } from '../Model/Site';
import DeploymentStatusTracker from '../Components/DeploymentStatusTracker';
import { DeploymentTrackerService } from '../Service/DeploymentTrackerService';
import axiosInstance from '../Core/RestHelper';

const formatDate = (dateStr: string | undefined) =>
  dateStr ? new Date(dateStr).toLocaleString() : 'N/A';

interface SSLConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  deploymentId: string;
  instanceId: string;
  publicIp?: string;
}

export const SSLConfigModal: React.FC<SSLConfigModalProps> = ({ isOpen, onClose, deploymentId,instanceId, publicIp }) => {
  const [step, setStep] = useState(1);
  const [domainName, setDomainName] = useState('');
  const [emailId, setEmailId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSSLConfiguration = async () => {
    if (!domainName || !emailId) {
      setError('Please fill in all required fields');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const requestBody = {
        domain_name: domainName,
        email_id: emailId
      };

      const response = await axiosInstance.post(
        `deploy/deployments/${deploymentId}/instance/${instanceId}`,
        requestBody
      );

      if (response.data.success) {
        setSuccess(true);
        setStep(3);
      } else {
        throw new Error('SSL configuration failed');
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || err.response?.data?.message || 'SSL configuration failed');
    } finally {
      setIsLoading(false);
    }
  };

  const resetModal = () => {
    setStep(1);
    setDomainName('');
    setEmailId('');
    setError('');
    setSuccess(false);
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Configure SSL Certificate</h3>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Progress Steps */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className={`flex items-center ${step >= 1 ? 'text-blue-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                step >= 1 ? 'bg-blue-100' : 'bg-gray-100'
              }`}>
                1
              </div>
              <span className="ml-2 text-sm font-medium">DNS Setup</span>
            </div>
            <div className={`flex items-center ${step >= 2 ? 'text-blue-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                step >= 2 ? 'bg-blue-100' : 'bg-gray-100'
              }`}>
                2
              </div>
              <span className="ml-2 text-sm font-medium">SSL Config</span>
            </div>
            <div className={`flex items-center ${step >= 3 ? 'text-green-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                step >= 3 ? 'bg-green-100' : 'bg-gray-100'
              }`}>
                3
              </div>
              <span className="ml-2 text-sm font-medium">Complete</span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {step === 1 && (
            <div className="space-y-6">
              <div>
                <h4 className="text-md font-semibold text-gray-900 mb-4">Step 1: Configure DNS Settings</h4>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <div className="flex items-start">
                    <svg className="w-5 h-5 text-blue-600 mt-0.5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                      <h5 className="font-medium text-blue-900">Public IP Address</h5>
                      <p className="text-sm text-blue-800 mt-1">
                        {publicIp || 'Please ensure your deployment has a public IP address'}
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <h5 className="font-medium text-gray-900">Instructions:</h5>
                  <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
                    <li>Login to your domain provider's control panel (GoDaddy, Namecheap, Cloudflare, etc.)</li>
                    <li>Navigate to the DNS Management section</li>
                    <li>Create or update an A record for your domain:</li>
                  </ol>
                  
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <span className="font-medium text-gray-900">Type:</span>
                        <p className="text-gray-700">A</p>
                      </div>
                      <div>
                        <span className="font-medium text-gray-900">Name/Host:</span>
                        <p className="text-gray-700">@ (or your subdomain)</p>
                      </div>
                      <div>
                        <span className="font-medium text-gray-900">Value:</span>
                        <p className="text-gray-700 font-mono bg-gray-100 px-2 py-1 rounded">
                          {publicIp || 'YOUR_PUBLIC_IP'}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="text-sm text-gray-600">
                    <p>💡 <strong>Tip:</strong> DNS changes can take up to 24 hours to propagate, but usually take 5-10 minutes.</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6">
              <div>
                <h4 className="text-md font-semibold text-gray-900 mb-4">Step 2: SSL Certificate Configuration</h4>
                <p className="text-sm text-gray-600 mb-6">
                  Please provide your domain name and email address to generate and configure the SSL certificate.
                </p>
                
                <div className="space-y-4">
                  <div>
                    <label htmlFor="domainName" className="block text-sm font-medium text-gray-700 mb-2">
                      Domain Name *
                    </label>
                    <input
                      type="text"
                      id="domainName"
                      value={domainName}
                      onChange={(e) => setDomainName(e.target.value)}
                      placeholder="example.com"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="emailId" className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="emailId"
                      value={emailId}
                      onChange={(e) => setEmailId(e.target.value)}
                      placeholder="admin@example.com"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
                
                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 mt-4">
                    <div className="flex items-center">
                      <svg className="w-5 h-5 text-red-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="text-sm text-red-800">{error}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="text-center space-y-6">
              <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-2">SSL Certificate Configured Successfully!</h4>
                <p className="text-sm text-gray-600">
                  Your SSL certificate has been generated and configured. Your site should now be accessible via HTTPS.
                </p>
              </div>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-sm text-green-800">
                  🎉 Your domain <strong>{domainName}</strong> is now secured with SSL!
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center p-6 border-t border-gray-200">
          <div>
            {step > 1 && step < 3 && (
              <button
                onClick={() => setStep(step - 1)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
              >
                Back
              </button>
            )}
          </div>
          
          <div className="flex space-x-3">
            {step < 3 && (
              <button
                onClick={handleClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
            )}
            
            {step === 1 && (
              <button
                onClick={() => setStep(2)}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors"
              >
                Next
              </button>
            )}
            
            {step === 2 && (
              <button
                onClick={handleSSLConfiguration}
                disabled={isLoading || !domainName || !emailId}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
              >
                {isLoading && (
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                )}
                {isLoading ? 'Configuring...' : 'Configure SSL'}
              </button>
            )}
            
            {step === 3 && (
              <button
                onClick={handleClose}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 transition-colors"
              >
                Done
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};